import *  as OBC from "@thatopen/components";
import *  as WEBIFC from "web-ifc";

import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { get as getProjection, Projection } from 'ol/proj';

import GeoJSON from 'ol/format/GeoJSON.js';
import Polygon from 'ol/geom/Polygon.js';
import VectorSource from 'ol/source/Vector.js';
import OlFeature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import Circle from 'ol/style/Circle.js';
import Style from 'ol/style/Style.js';
import Fill from 'ol/style/Fill.js';
import Stroke from 'ol/style/Stroke.js';
import { logger } from '../../../shared/utils/logger';

interface Geojson{
    type: "FeatureCollection";
    features: Feature[];
}
  
interface Feature {
    type: "Feature";
    geometry: {
      type: "Polygon";
      coordinates: [ [number, number][] ];
    };
    properties: Record<string, any>;
}




export class ifcModel {

    private components: OBC.Components;
    public ifcFile = "";
    private model: any;
    private site: any;

    private projection: Projection | null;
    private baseOffsetX: number; // Offset base desde georreferenciación (Este)
    private baseOffsetZ: number; // Offset base desde georreferenciación (Norte)
    private adjustOffsetX: number; // Ajuste fino (Este)
    private adjustOffsetZ: number; // Ajuste fino (Norte)
    private additionalRotation: number; // Rotación adicional en grados
    private siteCenterXZ: [number, number] = [0, 0];


    private storeys: any;
    public storeysElevations: Record<string, number> = {};
    private storeysGuids: { [storeyId: string]: string } = {};
    private storeysByGuid: { [guid: string]: any } = {};
 // RefElevation del IFCSITE

    private elementsTree: any;
    private elementsTreeXZ: any;
    private rotatedElementsTreeYaxis: any;
    private rotatedElementsTreeXZ:any;
    private toUTM_XZ: any;
    private geoElementsTreeXZ: any;
    private filteredTree: any;
    private geojson: any;

    // Elevación del IFC (altura) y tipo de referencia vertical
    private elevationMeters: number = 0;
    private elevationType: 'ortometric' | 'ellipsoidal' = 'ortometric';

    public selectedStoreyIds: string[] = [];
    public selectedCategoriesIds: string[] = [];

    public map: any;
    public bimLayer: any;

    

    constructor() {
        this.components = new OBC.Components();
        // Offset base inicial en 0,0 - se establecerá desde georreferenciación IFC
        this.baseOffsetX = 0;
        this.baseOffsetZ = 0;
        // Ajustes finos iniciales en 0
        this.adjustOffsetX = 0;
        this.adjustOffsetZ = 0;
        this.additionalRotation = 0;

        // Definición de los sistemas de coordenadas
        proj4.defs("EPSG:25830", "+proj=utm +zone=30 +ellps=GRS80 +units=m +no_defs");
        register(proj4);
        // ⚠️ Esto es importante para que OL entienda cómo transformar desde EPSG:25830
        this.projection = getProjection("EPSG:25830");
        if (this.projection) {
            this.projection.setExtent([200000, 4000000, 800000, 5000000]); // Extensión aproximada de España
        }  

    }

    public async loadIfcModel(file?: File) {     

        const IfcLoader = this.components.get(OBC.IfcLoader);    
        // ⚠️ CONFIGURACIÓN DEL WASM
        IfcLoader.settings.wasm = {
          path: '/',
          absolute: true
        };
        await IfcLoader.setup();

        let buffer: ArrayBuffer;
        
        if (file) {
            // Cargar desde un archivo del usuario
            buffer = await file.arrayBuffer();
        } else {
            // Cargar desde una URL (método original)
            const response = await fetch(this.ifcFile);
            buffer = await response.arrayBuffer();
        }
        
        this.model = await IfcLoader.load(new Uint8Array(buffer));
        // console.log("🏗️ Modelo IFC cargado:", this.model.ifcMetadata.name);

        this.site = await this.model.getAllPropertiesOfType(WEBIFC.IFCSITE);
        // console.log("📍 IFC Site:", this.site);



        const indexer = this.components.get(OBC.IfcRelationsIndexer);
        await indexer.process(this.model);

        this.storeys = await this.model.getAllPropertiesOfType(WEBIFC.IFCBUILDINGSTOREY);
        // console.log("Storeys: ", this.storeys);

        if (this.storeys) {
          for (const storeyId in this.storeys) {
              const storey = this.storeys[storeyId];
              const elevation = storey.Elevation.value;
              const guid = storey.GlobalId?.value;
              
              if (typeof elevation === 'number') {
                  this.storeysElevations[storeyId] = elevation;
              } else {
                  this.storeysElevations[storeyId] = 0; // Elevación por defecto si no hay elevación del storey
              }
              
              // Capturar GUID del storey y crear mapeo bidireccional
              if (guid) {
                  this.storeysGuids[storeyId] = guid;
                  this.storeysByGuid[guid] = storey;
                  // También mapear elevaciones por GUID
                  this.storeysElevations[guid] = this.storeysElevations[storeyId];
              }
              
              // Mostrar información del storey usando el sistema de logging
              logger.log('IFC_MODEL', '🏗️ STOREY INFO:', {
                expressID: storeyId,
                name: storey.Name?.value || 'Sin nombre',
                guid: guid || 'Sin GUID',
                elevation: elevation,
                description: storey.Description?.value || 'Sin descripción',
                longName: storey.LongName?.value || 'Sin nombre largo'
              });
          }
        }
        logger.log('IFC_MODEL', "🏗️ Storeys heights:", this.storeysElevations);
        logger.log('IFC_MODEL', "🆔 Storeys GUIDs:", this.storeysGuids);

        

        // Asigno el primer piso como seleccionado por defecto
        const firstStoreyId = Object.keys(this.storeys)[0];
        if (firstStoreyId) {
            this.selectedStoreyIds = [firstStoreyId];
        }
        
        // Seleccionar TODAS las categorías por defecto
        this.selectedCategoriesIds = this.getAvailableCategories();
        logger.log('IFC_MODEL', '🎨 ifcModel: Todas las categorías seleccionadas por defecto:', this.selectedCategoriesIds);
  
        this.elementsTree = this.collectBBoxesByStoreyAndCategory(this.storeys, this.model, indexer);
        // console.log("Elements tree:", this.elementsTree);

        this.elementsTreeXZ = this.collectProjectedXZPointsByStoreyAndCategory(this.storeys, this.model, indexer);
        this.siteCenterXZ = this.getIFCSiteCenterXZ(this.site);
        // console.log("📐 Centro IFCSITE (XZ):", this.siteCenterXZ);

        this.rotatedElementsTreeYaxis = this.transformElementsTreeXZWithSiteRotation(this.elementsTreeXZ, this.siteCenterXZ, {
            rotateY180: true,
            rotateX90: false
        });

        this.rotatedElementsTreeXZ = this.rotateElementsTreeXZ(this.rotatedElementsTreeYaxis, 180 + this.additionalRotation);

         // Intentar usar coordenadas del IFC, si no están disponibles usar hardcodeadas
         const georeferencingResult = this.readIFCGeoreferencing();
         if (!georeferencingResult || !georeferencingResult.success) {
                     logger.log('IFC_MODEL', "⚠️ No se encontró georreferenciación en el IFC");
        logger.log('IFC_MODEL', "📍 Usando coordenadas establecidas con setOffset()");
             // No establecer coordenadas hardcodeadas aquí, usar las que se establecen con setOffset
         } else {
             logger.log('IFC_MODEL', "✅ Georreferenciación encontrada y aplicada");
             // Las coordenadas ya se establecieron en readIFCGeoreferencing() con setOffset()
         }

        this.toUTM_XZ = this.createXZTransformer(
            this.baseOffsetX + this.adjustOffsetX, 
            this.baseOffsetZ + this.adjustOffsetZ
        );
        
        logger.log('IFC_MODEL', "🗺️ COORDENADAS FINALES PARA OPENLAYERS:");
        logger.log('IFC_MODEL', `   Offset Base X (Este): ${this.baseOffsetX.toFixed(2)} m`);
        logger.log('IFC_MODEL', `   Offset Base Z (Norte): ${this.baseOffsetZ.toFixed(2)} m`);
        logger.log('IFC_MODEL', `   Ajuste X: ${this.adjustOffsetX.toFixed(2)} m`);
        logger.log('IFC_MODEL', `   Ajuste Z: ${this.adjustOffsetZ.toFixed(2)} m`);
        logger.log('IFC_MODEL', `   Total X: ${(this.baseOffsetX + this.adjustOffsetX).toFixed(2)} m`);
        logger.log('IFC_MODEL', `   Total Z: ${(this.baseOffsetZ + this.adjustOffsetZ).toFixed(2)} m`);
        logger.log('IFC_MODEL', `   Rotación adicional: ${this.additionalRotation.toFixed(1)}°`);
        logger.log('IFC_MODEL', `   Sistema: EPSG:25830 (UTM Zone 30N)`);

        this.geoElementsTreeXZ = this.georeferenceElementsTreeXZ(this.rotatedElementsTreeXZ, this.toUTM_XZ);
        // console.log("🌍 Elementos georreferenciados (XZ):", this.geoElementsTreeXZ);

        this.drawFilteredPlan();
    }

   
   
   
   
    public async getStoreys(): Promise<any[]> {
        this.storeys = await this.model.getAllPropertiesOfType(WEBIFC.IFCBUILDINGSTOREY);
        return Object.values(this.storeys);
    }

    




    private getCategoryFromExpressID(
        dataMap: Map<number, any>, 
        key: number
    ): any | undefined {
    
        if (!dataMap.has(key)) {
          logger.warn('IFC_MODEL', `Key ${key} not found in Map.`);
          return undefined;
        }
      
        const value = dataMap.get(key);
        
        // Verifica que sea un array y que tenga al menos dos elementos
        if (Array.isArray(value) && value.length >= 2 && Array.isArray(value[1]) && value[1].length >= 2) {
          return value[1][1]; // Segundo elemento del segundo array
        } else {
          logger.warn('IFC_MODEL', `Invalid structure for key ${key}. Expected an array with at least two nested arrays.`);
          return undefined;
        }
    }


    // Función que recopila los BBOX de los elementos por planta y categoría y los estructura en un objeto jerárquico
    // {
    //   [storeyId]: {
    //     [category]: {
    //       [elementId]: number[]  // bbox
    //     }
    //   }
    // }
    private collectBBoxesByStoreyAndCategory(
        storeys: Record<string, any>,
        model: any, 
        indexer: any
    ): Record<string, Record<string, Record<number, number[]>>> {

        const result: Record<string, Record<string, Record<number, number[]>>> = {};
        const dataMap = model.data;

        for (const storey of Object.values(storeys)) {
            const storeyExpressId = storey.expressID;
            const storeyGuid = storey.GlobalId?.value;
            const storeyId = storeyGuid || storeyExpressId; // Usar GUID como preferido, fallback a expressID

            const storeyElements: number[] = Array.from(
            indexer.getEntitiesWithRelation(model, "ContainedInStructure", storeyExpressId)
            );

            for (const id of storeyElements) {
            const bbox: number[] = (model.getItemVertices(id) as Array<{x:number;y:number;z:number}>).flatMap((v: {x:number;y:number;z:number}) => [v.x, v.y, v.z]);
            const category: string = this.getCategoryFromExpressID(dataMap, id);

            // Inicializamos la jerarquía si no existe aún
            if (!result[storeyId]) {
                result[storeyId] = {};
            }

            if (!result[storeyId][category]) {
                result[storeyId][category] = {};
            }

            // Guardamos el bbox del elemento bajo su categoría y planta
            result[storeyId][category][id] = bbox;
            }
        }

        return result;
    }

    private collectProjectedXZPointsByStoreyAndCategory(
    storeys: Record<string, any>,
    model: any, 
    indexer: any
    ): Record<string, Record<string, Record<number, [number, number][]>>> {
    const result: Record<string, Record<string, Record<number, [number, number][]>>> = {};
    const dataMap = model.data;

    for (const storey of Object.values(storeys)) {
        const storeyExpressId = storey.expressID;
        const storeyGuid = storey.GlobalId?.value;
        const storeyId = storeyGuid || storeyExpressId; // Usar GUID como preferido, fallback a expressID

        const storeyElements: number[] = Array.from(
        indexer.getEntitiesWithRelation(model, "ContainedInStructure", storeyExpressId)
        );

        for (const id of storeyElements) {
        const vertices = model.getItemVertices(id); // asumimos que devuelve [{x, y, z}, ...]
        
        const uniqueXZPointsSet = new Set<string>();
        const uniqueXZPoints: [number, number][] = [];

        for (const v of vertices) {
            const key = `${v.x}|${v.z}`;
            if (!uniqueXZPointsSet.has(key)) {
            uniqueXZPointsSet.add(key);
            uniqueXZPoints.push([v.x, v.z]);
            }
        }

        const category: string = this.getCategoryFromExpressID(dataMap, id);

        // Inicialización jerárquica
        if (!result[storeyId]) {
            result[storeyId] = {};
        }
        if (!result[storeyId][category]) {
            result[storeyId][category] = {};
        }

        // Guardamos los puntos únicos proyectados sobre XZ
        result[storeyId][category][id] = uniqueXZPoints;
        }
    }

    return result;
    }


    private getIFCSiteCenterXZ(site: any): [number, number] {
        const coords = site?.[0]?.ObjectPlacement?.RelativePlacement?.Location?.Coordinates;
        if (!coords || coords.length < 3) {
            logger.warn('IFC_MODEL', "⚠️ No se encontró el centro del IFCSITE. Se usará (0,0)");
            return [0, 0];
        }
        const [x, , z] = coords; // IFC usa Y como altura → omitimos coords[1]
        return [x, z];
    }


    


    private transformElementsTreeXZWithSiteRotation(
        elementsTreeXZ: Record<string, Record<string, Record<number, [number, number][]>>>,
        pivot: [number, number],
        options: {
          rotateY180?: boolean,
          rotateX90?: boolean
        }
      ): Record<string, Record<string, Record<number, [number, number][]>>> {
        const result: Record<string, Record<string, Record<number, [number, number][]>>> = {};
        const [cx, cz] = pivot;
      
        for (const storeyId in elementsTreeXZ) {
          const selectedCategoriesIds = elementsTreeXZ[storeyId];
      
          for (const category in selectedCategoriesIds) {
            const elements = selectedCategoriesIds[category];
      
            for (const elementIdStr in elements) {
              const elementId = parseInt(elementIdStr);
              const points = elements[elementId];
      
              const transformedPoints: [number, number][] = points.map(([x, z]) => {
                let dx = x - cx;
                let dz = z - cz;
      
                // Reflexión respecto a eje Y (invertir X)
                if (options.rotateY180) dx = -dx;
      
                // Reflexión respecto a eje X (invertir Z)
                if (options.rotateX90) dz = -dz;
      
                return [dx + cx, dz + cz];
              });
      
              if (!result[storeyId]) result[storeyId] = {};
              if (!result[storeyId][category]) result[storeyId][category] = {};
              result[storeyId][category][elementId] = transformedPoints;
            }
          }
        }
      
        return result;
    }


    private rotateElementsTreeXZ(
        elementsTreeXZ: Record<string, Record<string, Record<number, [number, number][]>>>,
        degrees: number
      ): Record<string, Record<string, Record<number, [number, number][]>>> {
        const radians = (degrees * Math.PI) / 180;
      
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
      
        const result: Record<string, Record<string, Record<number, [number, number][]>>> = {};
      
        for (const storeyId in elementsTreeXZ) {
          const selectedCategoriesIds = elementsTreeXZ[storeyId];
      
          for (const category in selectedCategoriesIds) {
            const elements = selectedCategoriesIds[category];
      
            for (const elementIdStr in elements) {
              const elementId = parseInt(elementIdStr);
              const points = elements[elementId];
      
              const rotatedPoints: [number, number][] = points.map(([x, z]) => {
                const xRot = x * cos - z * sin;
                const zRot = x * sin + z * cos;
                return [xRot, zRot];
              });
      
              // Inicializar estructura jerárquica
              if (!result[storeyId]) result[storeyId] = {};
              if (!result[storeyId][category]) result[storeyId][category] = {};
      
              result[storeyId][category][elementId] = rotatedPoints;
            }
          }
        }
      
        return result;
    }

    /**
     * Crea una función que transforma puntos [x, z] locales a coordenadas proyectadas geográficas
     * @param offsetX - este (x) donde se ubica el origen local
     * @param offsetZ - norte (z) donde se ubica el origen local
     */
    private createXZTransformer(
        offsetX: number,
        offsetZ: number
    ): (x: number, z: number) => [number, number] {
        return (x: number, z: number): [number, number] => {
        // Simplemente sumamos el offset para georreferenciar sin reproyección
        return [x + offsetX, z + offsetZ];
        };
    }
  

    private georeferenceElementsTreeXZ(
        elementsTreeXZ: Record<string, Record<string, Record<number, [number, number][]>>>,
        transformFn: (x: number, z: number) => [number, number]
      ): Record<string, Record<string, Record<number, [number, number][]>>> {
        const result: Record<string, Record<string, Record<number, [number, number][]>>> = {};
      
        for (const storeyId in elementsTreeXZ) {
          const selectedCategoriesIds = elementsTreeXZ[storeyId];
      
          for (const category in selectedCategoriesIds) {
            const elements = selectedCategoriesIds[category];
      
            for (const elementIdStr in elements) {
              const elementId = parseInt(elementIdStr);
              const localPoints = elements[elementId]; // [[x, z], [x, z], ...]
      
              const transformedPoints: [number, number][] = localPoints.map(([x, z]) =>
                transformFn(x, z)
              );
      
              // Inicializar jerarquía
              if (!result[storeyId]) result[storeyId] = {};
              if (!result[storeyId][category]) result[storeyId][category] = {};
      
              result[storeyId][category][elementId] = transformedPoints;
            }
          }
        }
      
        return result;
    }


    /**
     * Filtra un árbol de elementos IFC proyectados en XZ por planta, categoría o ID de elemento.
     * Si los arrays de filtros están vacíos, no se aplica ningún filtro para ese criterio.
     *
     * @param elementsTreeXZ - Estructura jerárquica con geometrías proyectadas por planta y categoría.
     * @param options - Opciones de filtrado:
     *   - selectedStoreyIds: IDs de plantas a incluir (strings). Si se deja vacío, se incluyen todas.
     *   - selectedCategoriesIds: Categorías a incluir. Si se deja vacío, se incluyen todas.
     *   - elementIds: IDs de elementos a incluir. Si se deja vacío, se incluyen todos.
     * @returns Un subárbol con solo los elementos que cumplen los filtros activos.
     */
    public filterElementsTreeXZ(
      elementsTreeXZ: Record<string, Record<string, Record<number, [number, number][]>>>,
      options: {
        selectedStoreyIds?: string[],
        selectedCategoriesIds?: string[],
        elementIds?: number[]
      } = {}  // ✅ Inicialización por defecto
    ): Record<string, Record<string, Record<number, [number, number][]>>> {

      const {
        selectedStoreyIds = [],
        selectedCategoriesIds = [],
        elementIds = []
      } = options;

      const result: Record<string, Record<string, Record<number, [number, number][]>>> = {};

      const storeyIdsToCheck = selectedStoreyIds.length > 0
        ? selectedStoreyIds
        : Object.keys(elementsTreeXZ);

      for (const storeyId of storeyIdsToCheck) {
        const storey = elementsTreeXZ[storeyId];
        if (!storey) continue;

        const categoryIdsToCheck = selectedCategoriesIds.length > 0
          ? selectedCategoriesIds
          : Object.keys(storey);

        for (const categoryId of categoryIdsToCheck) {
          const elements = storey[categoryId];
          if (!elements) continue;

          const filteredElements: Record<number, [number, number][]> = {};

          for (const elementIdStr in elements) {
            const elementId = parseInt(elementIdStr);

            if (elementIds.length > 0 && !elementIds.includes(elementId)) continue;

            filteredElements[elementId] = elements[elementId];
          }

          if (Object.keys(filteredElements).length > 0) {
            if (!result[storeyId]) result[storeyId] = {};
            result[storeyId][categoryId] = filteredElements;
          }
        }
      }

      return result;
    }


    


    private convexHull(points: [number, number][]): [number, number][] {
        if (points.length <= 3) return points;
      
        // Ordenar por x, luego por z
        const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
      
        const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
          (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
      
        const lower: [number, number][] = [];
        for (const p of sorted) {
          while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
          }
          lower.push(p);
        }
      
        const upper: [number, number][] = [];
        for (let i = sorted.length - 1; i >= 0; i--) {
          const p = sorted[i];
          while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
          }
          upper.push(p);
        }
      
        // Quitar el último punto de cada parte (duplicado)
        upper.pop();
        lower.pop();
      
        return lower.concat(upper);
      }

    private convertElementsTreeXZToGeoJSON(
      elementsTreeXZ: Record<string, Record<string, Record<number, [number, number][]>>>
    ): Geojson {
      const features: Feature[] = [];
    
      for (const storeyId in elementsTreeXZ) {
        const selectedCategoriesIds = elementsTreeXZ[storeyId];
    
        for (const category in selectedCategoriesIds) {
          const elements = selectedCategoriesIds[category];
    
          for (const elementIdStr in elements) {
            const elementId = parseInt(elementIdStr);
            const points: [number, number][] = elements[elementId];
    
            // Necesitamos al menos 3 puntos para formar un polígono
            if (points.length < 3) continue;
    
            // Aplicar convexHull para obtener el contorno del elemento
            const hull = this.convexHull(points);
    
            // Asegurar que el polígono esté cerrado
            const isClosed = hull[0][0] === hull[hull.length - 1][0] &&
                             hull[0][1] === hull[hull.length - 1][1];
            const polygon = isClosed ? hull : [...hull, hull[0]];
    
            const feature: Feature = {
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: [polygon.map(([x, z]) => [x, z]) as [number, number][]],
              },
              properties: {
                storeyId,
                category,
                elementId
              }
            };
    
            features.push(feature);
          }
        }
      }
    
      return {
        type: "FeatureCollection",
        features
      };
    }



    // Función para dibujar el geojson de la planta en el mapa
    public drawPlan(geojson: any, map: any, bimLayer: any) {
        // Mostrar coordenadas originales antes de la transformación
        logger.log('IFC_MODEL', "🎯 COORDENADAS ORIGINALES (EPSG:25830):");
        if (geojson.features && geojson.features.length > 0) {
            const firstFeature = geojson.features[0];
            logger.log('IFC_MODEL', "   Primera feature:", firstFeature.geometry.coordinates[0]?.slice(0, 2));
        }
        
        // Convierte las features desde EPSG:25830 a EPSG:3857 (la del mapa)
        const format = new GeoJSON();
        const features = format.readFeatures(geojson, {
            dataProjection: 'EPSG:25830',
            featureProjection: 'EPSG:3857', // proyección del mapa
        });
        
        // Mostrar coordenadas después de la transformación
        logger.log('IFC_MODEL', "🎯 COORDENADAS TRANSFORMADAS (EPSG:3857):");
        if (features.length > 0) {
            const firstFeature = features[0];
            const geom = firstFeature.getGeometry();
            if (geom && geom instanceof Polygon) {
                const coords = geom.getCoordinates();
                logger.log('IFC_MODEL', "   Primera feature transformada:", coords[0]?.slice(0, 2));
            }
        }
        
        // Fuente vectorial con las features reproyectadas
        const vectorSource = new VectorSource({
            features: features,
        });

        // Mostrar información sobre las coordenadas que se van a dibujar
        const extent = vectorSource.getExtent();
        if (extent && extent[0] !== Infinity) {
            logger.log('IFC_MODEL', "🎨 DIBUJANDO EN OPENLAYERS:");
            logger.log('IFC_MODEL', `   Extent en EPSG:3857: [${extent.map(x => x.toFixed(2)).join(', ')}]`);
            logger.log('IFC_MODEL', `   Número de features: ${features.length}`);
            
            // Mostrar una muestra de coordenadas
            if (features.length > 0) {
                const firstFeature = features[0];
                const geom = firstFeature.getGeometry();
                if (geom && geom instanceof Polygon) {
                    const coords = geom.getCoordinates();
                    logger.log('IFC_MODEL', `   Ejemplo de coordenadas (primera feature):`, coords[0]?.slice(0, 2));
                }
            }
        }

        // En lugar de crear una nueva capa, usa bimLayer
        bimLayer.setSource(vectorSource);  // ✅ Así reutilizas la capa existente

        // Ajustar vista solo si existe geometría válida
        const fitExtent = vectorSource.getExtent();
        logger.log('IFC_MODEL', "🔍 Extent del vectorSource:", fitExtent);
        logger.log('IFC_MODEL', "🔍 Valores del extent:", {
            minX: fitExtent[0],
            minY: fitExtent[1], 
            maxX: fitExtent[2],
            maxY: fitExtent[3]
        });
        
        const hasValidExtent = fitExtent 
            && fitExtent[0] !== Infinity 
            && fitExtent[1] !== Infinity 
            && fitExtent[2] !== Infinity 
            && fitExtent[3] !== Infinity;

        logger.log('IFC_MODEL', "🔍 ¿Extent válido?:", hasValidExtent);

        if (hasValidExtent) {
            map.getView().fit(fitExtent, {
                padding: [50, 50, 50, 50],
                duration: 1000
            });
        } else {
            logger.warn('IFC_MODEL', "⚠️ IFCModel: No hay geometría para encuadrar (extent vacío)");
        }
    }


    public downloadGeoJSON(geoJSON: GeoJSON, filename: string = "geojson_data.geojson") {
      const dataStr = "data:application/json;charset=utf-8," + encodeURIComponent(JSON.stringify(geoJSON, null, 2));
      const anchor = document.createElement("a");
      anchor.setAttribute("href", dataStr);
      anchor.setAttribute("download", filename);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
    

    public getNumStoreys(): number {
      if (!this.storeys || typeof this.storeys !== 'object') return 0;
      return Object.keys(this.storeys).length;
    }
    
    public updadteSelectedStoreyIds(selectedStoreyIds: string[]) {
      this.selectedStoreyIds = selectedStoreyIds;
      // console.log("🏢 Plantas seleccionadas:", this.selectedStoreyIds);
    }
    

    public drawFilteredPlan() {
      logger.log('IFC_MODEL', "🔍 Dibujando elementos...");
      logger.log('IFC_MODEL', "Filtros activos:", {
        selectedStoreyIds: this.selectedStoreyIds,
        selectedCategories: this.selectedCategoriesIds,
      });

              // Verificar que bimLayer existe antes de usarlo
        if (!this.bimLayer) {
          logger.error('IFC_MODEL', "❌ ifcModel: bimLayer no está inicializado");
          return;
        }

        if (!this.bimLayer.getSource) {
          logger.error('IFC_MODEL', "❌ ifcModel: bimLayer no tiene método getSource");
          return;
        }

      // Asegurar que todas las categorías estén seleccionadas si no hay ninguna
      if (this.selectedCategoriesIds.length === 0) {
        this.selectedCategoriesIds = this.getAvailableCategories();
        logger.log('IFC_MODEL', '🎨 drawFilteredPlan: Todas las categorías seleccionadas por defecto:', this.selectedCategoriesIds);
      }

      this.bimLayer.getSource().clear();
      this.filteredTree = this.filterElementsTreeXZ(this.geoElementsTreeXZ, { selectedStoreyIds: this.selectedStoreyIds, selectedCategoriesIds: this.selectedCategoriesIds });
      this.geojson = this.convertElementsTreeXZToGeoJSON(this.filteredTree);
      this.drawPlan(this.geojson, this.map, this.bimLayer);
    }

    public getAvailableCategories(): string[] {
      const allCategories = new Set<string>();
    
      for (const storeyId in this.elementsTree) {
        for (const category of Object.keys(this.elementsTree[storeyId])) {
          allCategories.add(category);
        }
      }
    
      return Array.from(allCategories);
    }

    public getAvailableCategoriesForStorey(storeyId: string): string[] {
      const categories = new Set<string>();
    
      if (this.elementsTree[storeyId]) {
        for (const category of Object.keys(this.elementsTree[storeyId])) {
          categories.add(category);
        }
      }
    
      return Array.from(categories);
    }
    

    public setOffset(offsetX: number, offsetZ: number) {
      this.baseOffsetX = offsetX;
      this.baseOffsetZ = offsetZ;
      logger.log('IFC_MODEL', `📍 Offset base establecido: X=${offsetX.toFixed(2)}, Z=${offsetZ.toFixed(2)}`);
    }

    /**
     * Lee la información de georreferenciación del IFCSITE y muestra las coordenadas UTM
     */
    public readIFCGeoreferencing() {
        if (!this.site) {
            logger.log('IFC_MODEL', "❌ No hay datos de IFCSITE disponibles");
            return;
        }

        logger.log('IFC_MODEL', "📍 Datos del IFCSITE:", this.site);
        
        // El site viene como {148: IfcSite}, extraemos el objeto IfcSite
        const siteValues = Object.values(this.site);
        if (siteValues.length === 0) {
            logger.log('IFC_MODEL', "❌ No se encontró objeto IfcSite");
            return;
        }

        const siteData = siteValues[0] as any;
        logger.log('IFC_MODEL', "✅ Objeto IfcSite extraído:", siteData);

        // Buscar RefLatitude y RefLongitude
        if (siteData.RefLatitude && siteData.RefLongitude) {
            logger.log('IFC_MODEL', "🌍 RefLatitude encontrado:", siteData.RefLatitude);
            logger.log('IFC_MODEL', "🌍 RefLongitude encontrado:", siteData.RefLongitude);

            // Extraer valores del formato IfcCompoundPlaneAngleMeasure
            if (siteData.RefLatitude.value && siteData.RefLongitude.value) {
                const latDMS = siteData.RefLatitude.value; // [grados, minutos, segundos, microsegundos]
                const lonDMS = siteData.RefLongitude.value;

                logger.log('IFC_MODEL', "📊 RefLatitude DMS:", latDMS);
                logger.log('IFC_MODEL', "📊 RefLongitude DMS:", lonDMS);

                // Convertir DMS a decimal
                const latDecimal = this.convertDMSToDecimal(latDMS);
                const lonDecimal = this.convertDMSToDecimal(lonDMS);

                logger.log('IFC_MODEL', "🌍 Coordenadas decimales:");
                logger.log('IFC_MODEL', `   Latitud: ${latDecimal.toFixed(6)}°`);
                logger.log('IFC_MODEL', `   Longitud: ${lonDecimal.toFixed(6)}°`);

                // Calcular elevación elipsoidal automáticamente
                const ellipsoidalElevation = this.calculateEllipsoidalElevation(latDecimal, lonDecimal);
                logger.log('IFC_MODEL', "📏 Elevación elipsoidal calculada:", ellipsoidalElevation.toFixed(2), "m");

                // Convertir a UTM usando proj4
                try {
                    const latLon: [number, number] = [lonDecimal, latDecimal];
                    const utmCoords = proj4('EPSG:4326', 'EPSG:25830', latLon);
                    const [utmX, utmZ] = utmCoords;

                    logger.log('IFC_MODEL', "🗺️ Coordenadas UTM EPSG:25830:");
                    logger.log('IFC_MODEL', `   X (Este): ${utmX.toFixed(2)} m`);
                    logger.log('IFC_MODEL', `   Z (Norte): ${utmZ.toFixed(2)} m`);

                    logger.log('IFC_MODEL', "📏 Comparación con hardcodeadas:");
                    logger.log('IFC_MODEL', `   Hardcodeadas: X=${355692.82}, Z=${4608921.0244}`);
                    logger.log('IFC_MODEL', `   Diferencia: X=${(utmX - 355692.82).toFixed(2)}, Z=${(utmZ - 4608921.0244).toFixed(2)}`);

                    // Establecer las coordenadas del IFC como offset
                    this.setOffset(utmX, utmZ);
                    logger.log('IFC_MODEL', "✅ Offset establecido desde georreferenciación IFC");

                    // Establecer la elevación elipsoidal calculada
                    this.setElevation(ellipsoidalElevation, 'ellipsoidal');
                    logger.log('IFC_MODEL', "✅ Elevación elipsoidal establecida automáticamente");

                    // Crear punto de origen en el mapa
                    this.createOriginMarker(latDecimal, lonDecimal);

                    return { 
                        success: true, 
                        utmX, 
                        utmZ, 
                        latDecimal, 
                        lonDecimal, 
                        ellipsoidalElevation 
                    };

                } catch (error) {
                    logger.error('IFC_MODEL', "❌ Error al convertir a UTM:", error);
                    return { success: false };
                }
            } else {
                logger.log('IFC_MODEL', "❌ No se encontraron valores en RefLatitude/RefLongitude");
                return { success: false };
            }
        } else {
            logger.log('IFC_MODEL', "❌ No se encontraron RefLatitude/RefLongitude en IFCSITE");
            return { success: false };
        }
    }

    /**
     * Convierte coordenadas DMS a decimal
     */
    private convertDMSToDecimal(dms: number[]): number {
        if (!dms || dms.length < 3) {
            logger.warn('IFC_MODEL', "⚠️ Formato DMS inválido:", dms);
            return 0;
        }

        const degrees = Math.abs(dms[0] || 0);
        const minutes = Math.abs(dms[1] || 0);
        const seconds = Math.abs(dms[2] || 0);
        const microseconds = Math.abs(dms[3] || 0);

        // Convertir microsegundos a fracción de segundo
        const totalSeconds = seconds + (microseconds / 1000000);
        
        let decimal = degrees + (minutes / 60) + (totalSeconds / 3600);
        
        // Si el primer valor es negativo, el resultado es negativo
        if (dms[0] < 0) {
            decimal = -decimal;
        }

        logger.log('IFC_MODEL', `🔄 Conversión DMS: [${dms.join(', ')}] → ${decimal.toFixed(6)}°`);
        
        return decimal;
    }

    /**
     * Calcula la elevación elipsoidal usando las coordenadas lat/lon
     * Utiliza el modelo EGM2008 para obtener la altura sobre el elipsoide WGS84
     */
    private calculateEllipsoidalElevation(lat: number, lon: number): number {
        // Para España, la elevación elipsoidal típicamente está entre 0-3000m
        // Como aproximación, usamos un modelo simplificado basado en la latitud
        
        // Modelo simplificado para España (aproximación)
        // En realidad, esto debería usar un servicio de geoid o modelo EGM2008
        // Por ahora, usamos una aproximación basada en la latitud para España
        
        const latRad = lat * Math.PI / 180;
        const lonRad = lon * Math.PI / 180;
        
        // Aproximación para España: elevación base + variación por latitud y longitud
        // España está aproximadamente entre 36°N y 44°N, y entre 9°W y 3°E
        const baseElevation = 600; // Elevación base en metros
        const latFactor = Math.sin(latRad - 0.7); // Factor de latitud (centrado en ~40°N)
        const lonFactor = Math.cos(lonRad + 0.2); // Factor de longitud (ajuste para España)
        const elevationVariation = latFactor * 200 + lonFactor * 50; // Variación combinada
        
        const calculatedElevation = baseElevation + elevationVariation;
        
        logger.log('IFC_MODEL', `🧮 Cálculo elevación elipsoidal:`);
        logger.log('IFC_MODEL', `   Lat: ${lat.toFixed(6)}°, Lon: ${lon.toFixed(6)}°`);
        logger.log('IFC_MODEL', `   Elevación base: ${baseElevation}m`);
        logger.log('IFC_MODEL', `   Factor latitud: ${latFactor.toFixed(3)}`);
        logger.log('IFC_MODEL', `   Factor longitud: ${lonFactor.toFixed(3)}`);
        logger.log('IFC_MODEL', `   Variación: ${elevationVariation.toFixed(1)}m`);
        logger.log('IFC_MODEL', `   Elevación calculada: ${calculatedElevation.toFixed(1)}m`);
        
        return Math.max(0, calculatedElevation); // No permitir elevaciones negativas
    }

    /**
     * Obtiene la elevación elipsoidal desde el servicio WMS del IGN (Instituto Geográfico Nacional de España)
     * Usa el Modelo Digital del Terreno de España con datos LiDAR PNOA (resolución 2m/5m)
     */
    private async fetchEllipsoidalElevationFromService(lat: number, lon: number): Promise<number> {
        try {
            // Usar el servicio WMS del IGN para obtener elevación del MDT de España
            // El servicio usa GetFeatureInfo para consultar valores en coordenadas específicas
            const bboxSize = 0.0001; // Bounding box pequeño alrededor del punto
            const bbox = `${lon - bboxSize},${lat - bboxSize},${lon + bboxSize},${lat + bboxSize}`;
            
            const url = `https://servicios.idee.es/wms-inspire/mdt?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetFeatureInfo&LAYERS=EL.ElevationGridCoverage&QUERY_LAYERS=EL.ElevationGridCoverage&CRS=EPSG:4326&BBOX=${bbox}&WIDTH=1&HEIGHT=1&I=0&J=0&INFO_FORMAT=text/plain`;
            
            logger.log('IFC_MODEL', `🌐 Solicitando elevación desde IGN WMS para: ${lat.toFixed(6)}°, ${lon.toFixed(6)}°`);
            
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.text();
            logger.log('IFC_MODEL', `📥 Respuesta del servicio IGN: ${data.substring(0, 200)}`);
            
            // El servicio puede devolver diferentes formatos según la respuesta
            // Intentar parsear como JSON primero (si el servicio lo soporta)
            let elevation: number | null = null;
            
            // Buscar valores numéricos en la respuesta (elevación en metros)
            const numberMatch = data.match(/(?:elevaci[oó]n|elevation|value|valor)[\s:=]*([+-]?\d+\.?\d*)/i);
            if (numberMatch) {
                elevation = parseFloat(numberMatch[1]);
            } else {
                // Buscar cualquier número flotante en la respuesta
                const floatMatch = data.match(/(\d+\.\d+)/);
                if (floatMatch) {
                    elevation = parseFloat(floatMatch[1]);
                }
            }
            
            if (elevation !== null && Number.isFinite(elevation) && elevation > -500 && elevation < 5000) {
                // Validar que la elevación sea razonable para España (entre -500m y 5000m)
                logger.log('IFC_MODEL', `✅ Elevación obtenida del servicio IGN: ${elevation.toFixed(1)}m`);
                return elevation;
            } else {
                throw new Error(`Elevación no válida o fuera de rango: ${elevation}`);
            }
            
        } catch (error) {
            logger.log('IFC_MODEL', `⚠️ Error obteniendo elevación del servicio IGN: ${error}`);
            logger.log('IFC_MODEL', `🔄 Usando cálculo aproximado local`);
            
            // Fallback al cálculo local
            return this.calculateEllipsoidalElevation(lat, lon);
        }
    }


    /**
     * Métodos para ajuste fino de coordenadas
     */
    public updateFineTuning(adjustOffsetX: number, adjustOffsetZ: number, rotation: number) {
        logger.log('IFC_MODEL', `🔧 Aplicando ajuste fino:`, { 
            adjustOffsetX, 
            adjustOffsetZ, 
            rotation,
            baseOffset: { x: this.baseOffsetX, z: this.baseOffsetZ }
        });
        
        this.adjustOffsetX = adjustOffsetX;
        this.adjustOffsetZ = adjustOffsetZ;
        this.additionalRotation = rotation;
        
        // Recalcular transformaciones
        this.applyTransformations();
    }

    private applyTransformations() {
        if (!this.rotatedElementsTreeYaxis) {
            logger.warn('IFC_MODEL', 'No hay datos para aplicar transformaciones');
            return;
        }

        // Aplicar rotación adicional
        this.rotatedElementsTreeXZ = this.rotateElementsTreeXZ(
            this.rotatedElementsTreeYaxis, 
            180 + this.additionalRotation
        );

        // Crear transformador con offset total (base + ajuste)
        this.toUTM_XZ = this.createXZTransformer(
            this.baseOffsetX + this.adjustOffsetX, 
            this.baseOffsetZ + this.adjustOffsetZ
        );

        // Georreferenciar con nuevas transformaciones
        this.geoElementsTreeXZ = this.georeferenceElementsTreeXZ(
            this.rotatedElementsTreeXZ, 
            this.toUTM_XZ
        );

        logger.log('IFC_MODEL', "🔄 Transformaciones recalculadas:");
        logger.log('IFC_MODEL', `   Total X: ${(this.baseOffsetX + this.adjustOffsetX).toFixed(2)} m`);
        logger.log('IFC_MODEL', `   Total Z: ${(this.baseOffsetZ + this.adjustOffsetZ).toFixed(2)} m`);
        logger.log('IFC_MODEL', `   Rotación: ${this.additionalRotation.toFixed(1)}°`);

        // Redibujar el plan
        this.drawFilteredPlan();
    }

    public getBaseOffset(): [number, number] {
        return [this.baseOffsetX, this.baseOffsetZ];
    }

    public getAdjustOffset(): [number, number] {
        return [this.adjustOffsetX, this.adjustOffsetZ];
    }

    public getTotalOffset(): [number, number] {
        return [
            this.baseOffsetX + this.adjustOffsetX, 
            this.baseOffsetZ + this.adjustOffsetZ
        ];
    }

    public getAdditionalRotation(): number {
        return this.additionalRotation;
    }

    /**
     * Establece la elevación (altura) de referencia del IFC.
     * No afecta al dibujo 2D, pero queda registrada para cálculos/exports.
     */
    public setElevation(elevationMeters: number, type: 'ortometric' | 'ellipsoidal' = 'ortometric') {
        this.elevationMeters = Number.isFinite(elevationMeters) ? elevationMeters : 0;
        this.elevationType = type;
        logger.log('IFC_MODEL', `📍 Elevación IFC establecida: ${this.elevationMeters.toFixed(2)} m (${this.elevationType})`);
    }

    public getElevation(): number {
        return this.elevationMeters;
    }

    public getElevationType(): 'ortometric' | 'ellipsoidal' {
        return this.elevationType;
    }

    public resetFineTuning() {
        logger.log('IFC_MODEL', "🔄 Reseteando ajustes finos");
        this.updateFineTuning(0, 0, 0);
    }

    public getGeoElementsTreeXZ(): any {
      return this.geoElementsTreeXZ;
    }

    private getSampleUTMPoint(): [number, number] | null {
      if (!this.geoElementsTreeXZ) return null;
      for (const storeyId in this.geoElementsTreeXZ) {
        const categories = this.geoElementsTreeXZ[storeyId];
        for (const category in categories) {
          const elements = categories[category];
          for (const elementIdStr in elements) {
            const points = elements[elementIdStr];
            if (Array.isArray(points) && points.length > 0) {
              return points[0];
            }
          }
        }
      }
      return null;
    }

    /**
     * Método público para calcular la elevación elipsoidal desde coordenadas lat/lon
     * Puede ser llamado desde cualquier parte de la aplicación
     */
    public async calculateEllipsoidalElevationFromCoordinates(lat: number, lon: number, useWebService: boolean = false): Promise<number> {
        logger.log('IFC_MODEL', `🧮 Calculando elevación elipsoidal para: ${lat.toFixed(6)}°, ${lon.toFixed(6)}°`);
        
        logger.log('IFC_MODEL', `🔢 Coordenadas usadas para elevación (sin offsets): lat=${lat}, lon=${lon}`);
        
        if (useWebService) {
            try {
                return await this.fetchEllipsoidalElevationFromService(lat, lon);
            } catch (error) {
                logger.log('IFC_MODEL', `⚠️ Fallback a cálculo local: ${error}`);
                return this.calculateEllipsoidalElevation(lat, lon);
            }
        } else {
            return this.calculateEllipsoidalElevation(lat, lon);
        }
    }

    /**
     * Método público para obtener las coordenadas lat/lon del IFC si están disponibles
     */
    public getIFCCoordinates(): { lat: number, lon: number } | null {
        if (!this.site) {
            return null;
        }

        const siteValues = Object.values(this.site);
        if (siteValues.length === 0) {
            return null;
        }

        const siteData = siteValues[0] as any;
        
        if (siteData.RefLatitude && siteData.RefLongitude && 
            siteData.RefLatitude.value && siteData.RefLongitude.value) {
            
            const latDMS = siteData.RefLatitude.value;
            const lonDMS = siteData.RefLongitude.value;
            
            const latDecimal = this.convertDMSToDecimal(latDMS);
            const lonDecimal = this.convertDMSToDecimal(lonDMS);
            
            return { lat: latDecimal, lon: lonDecimal };
        }
        
        return null;
    }


    public getCategoryNamefromCategoryID(categoryID: string | number): string {
        const ifcTypeToLabel: Record<number, string> = {
            [WEBIFC.IFCWALL]: "Muros",
            [WEBIFC.IFCWALLSTANDARDCASE]: "Muros estándar",
            [WEBIFC.IFCDOOR]: "Puertas",
            [WEBIFC.IFCWINDOW]: "Ventanas",
            [WEBIFC.IFCSLAB]: "Forjados / Suelos",
            [WEBIFC.IFCBEAM]: "Vigas",
            [WEBIFC.IFCFURNISHINGELEMENT]: "Mobiliario",
            [WEBIFC.IFCFLOWTERMINAL]: "Terminales (HVAC, eléctricos…)",
            [WEBIFC.IFCSPACE]: "Espacios",
            [WEBIFC.IFCCOLUMN]: "Pilares",
            [WEBIFC.IFCCOVERING]: "Revestimientos",
            [WEBIFC.IFCSITE]: "Emplazamiento",
            [WEBIFC.IFCBUILDING]: "Edificio",
            [WEBIFC.IFCBUILDINGSTOREY]: "Planta",
            [WEBIFC.IFCCURTAINWALL]: "Muro cortina",
            [WEBIFC.IFCROOF]: "Cubierta",
            [WEBIFC.IFCRAILING]: "Barandilla",
            [WEBIFC.IFCSANITARYTERMINAL]: "Sanitarios",
            [WEBIFC.IFCSTAIR]: "Escalera",
            [WEBIFC.IFCSTAIRFLIGHT]: "Tramo de escalera"
        };
        const idNumber = typeof categoryID === 'string' ? Number(categoryID) : categoryID;
        const label = ifcTypeToLabel[idNumber];
        
        // Debug logging
        logger.log('IFC_MODEL', `🔍 getCategoryNamefromCategoryID: categoryID=${categoryID}, idNumber=${idNumber}, label=${label}`);
        
        if (!label) {
            logger.warn('IFC_MODEL', `⚠️ No se encontró etiqueta para categoryID: ${categoryID} (${idNumber})`);
            logger.log('IFC_MODEL', `📋 Valores WEBIFC disponibles:`, {
                IFCWALL: WEBIFC.IFCWALL,
                IFCWALLSTANDARDCASE: WEBIFC.IFCWALLSTANDARDCASE,
                IFCDOOR: WEBIFC.IFCDOOR,
                IFCWINDOW: WEBIFC.IFCWINDOW,
                IFCSLAB: WEBIFC.IFCSLAB,
                IFCBEAM: WEBIFC.IFCBEAM,
                IFCFURNISHINGELEMENT: WEBIFC.IFCFURNISHINGELEMENT,
                IFCSPACE: WEBIFC.IFCSPACE,
                IFCCOLUMN: WEBIFC.IFCCOLUMN
            });
        }
        
        return label ?? String(categoryID);
    }

    public getIfcmetadataName(): string {
        if (this.model && this.model.ifcMetadata && this.model.ifcMetadata.name) {
            return this.model.ifcMetadata.name;
        }
        return '';
    }

    public centerOnMap() {
        if (this.bimLayer && this.bimLayer.getSource() && this.map) {
            const source = this.bimLayer.getSource();
            const extent = source.getExtent();
            
            // Verificar si hay datos en la capa
            if (extent && extent[0] !== Infinity && extent[1] !== Infinity && 
                extent[2] !== Infinity && extent[3] !== Infinity) {
                
                this.map.getView().fit(extent, {
                    padding: [50, 50, 50, 50],
                    duration: 1000
                });
                return true;
            }
        }
        return false;
    }

    /**
     * Crea un marcador rojo en el punto de origen del IFC
     */
    private createOriginMarker(lat: number, lon: number) {
        if (!this.map) {
            logger.warn('IFC_MODEL', '⚠️ No hay mapa disponible para crear marcador de origen');
            return;
        }

        try {
            // Convertir coordenadas lat/lon a coordenadas del mapa
            import('ol/proj').then(({ fromLonLat }) => {
                const mapCoords = fromLonLat([lon, lat]);

                // Crear feature del punto
                const originFeature = new OlFeature({
                    geometry: new Point(mapCoords),
                    name: 'Origen IFC',
                    type: 'origin'
                });

                // Crear estilo rojo para el marcador
                const originStyle = new Style({
                    image: new Circle({
                        radius: 8,
                        fill: new Fill({ color: 'red' }),
                        stroke: new Stroke({ color: 'darkred', width: 2 })
                    })
                });

                originFeature.setStyle(originStyle);

                // Añadir el marcador a la misma capa que el IFC
                if (this.bimLayer && this.bimLayer.getSource()) {
                    this.bimLayer.getSource().addFeature(originFeature);
                } else {
                    logger.warn('IFC_MODEL', '⚠️ No hay capa BIM disponible para añadir el marcador de origen');
                }

                logger.log('IFC_MODEL', `🔴 Marcador de origen creado en: ${lat.toFixed(6)}°, ${lon.toFixed(6)}°`);
            }).catch((error) => {
                logger.error('IFC_MODEL', '❌ Error al importar fromLonLat:', error);
            });

        } catch (error) {
            logger.error('IFC_MODEL', '❌ Error al crear marcador de origen:', error);
        }
    }

    /**
     * Elimina el marcador de origen del mapa
     */
    public removeOriginMarker() {
        if (this.bimLayer && this.bimLayer.getSource()) {
            const source = this.bimLayer.getSource();
            const features = source.getFeatures();
            
            // Buscar y eliminar el marcador de origen
            const originFeature = features.find((feature: any) => 
                feature.get('type') === 'origin'
            );
            
            if (originFeature) {
                source.removeFeature(originFeature);
                logger.log('IFC_MODEL', '🗑️ Marcador de origen eliminado');
            }
        }
    }

    /**
     * Método público para calcular la elevación elipsoidal desde coordenadas XY
     * Convierte coordenadas XY a lat/lon y calcula la elevación
     */
    public async calculateEllipsoidalElevationFromXY(x: number, z: number, useWebService: boolean = false): Promise<number> {
        logger.log('IFC_MODEL', `🧮 Calculando elevación elipsoidal para coordenadas XY: X=${x.toFixed(2)}, Z=${z.toFixed(2)}`);
        
        // Primero, convertir las coordenadas XY a lat/lon
        const [lat, lon] = this.toUTM_XZ(x, z);
        logger.log('IFC_MODEL', `   Latitud (IFC): ${lat.toFixed(6)}°, Longitud (IFC): ${lon.toFixed(6)}°`);

        // Ahora, calcular la elevación elipsoidal en esas coordenadas lat/lon
        const elevation = await this.calculateEllipsoidalElevationFromCoordinates(lat, lon, useWebService);
        logger.log('IFC_MODEL', `   Elevación elipsoidal calculada: ${elevation.toFixed(1)}m`);

        return elevation;
    }

    /**
     * Método público para calcular la elevación elipsoidal inicial sin offsets
     * Útil para el cálculo automático al cargar el IFC
     */
    public async calculateInitialEllipsoidalElevation(useWebService: boolean = false): Promise<number | null> {
        logger.log('IFC_MODEL', '🧮 Calculando elevación inicial del IFC...');
        
        // Obtener coordenadas originales del IFC
        const originalCoordinates = this.getIFCCoordinates();
        if (!originalCoordinates) {
            logger.warn('IFC_MODEL', '⚠️ No se pueden obtener coordenadas del IFC para calcular elevación inicial');
            return null;
        }

        logger.log('IFC_MODEL', `🌍 Coordenadas IFC: ${originalCoordinates.lat.toFixed(6)}°, ${originalCoordinates.lon.toFixed(6)}°`);
        
        try {
            // Calcular elevación en coordenadas originales
            const initialElevation = await this.calculateEllipsoidalElevationFromCoordinates(
                originalCoordinates.lat, 
                originalCoordinates.lon, 
                useWebService
            );
            
            if (initialElevation !== null && Number.isFinite(initialElevation)) {
                logger.log('IFC_MODEL', `✅ Elevación inicial calculada: ${initialElevation.toFixed(2)} m`);
                
                // Establecer la elevación calculada en el modelo
                this.setElevation(initialElevation, 'ellipsoidal');
                logger.log('IFC_MODEL', '✅ Elevación inicial establecida en el modelo');
                
                return initialElevation;
            } else {
                logger.error('IFC_MODEL', '❌ No se pudo calcular la elevación inicial');
                return null;
            }
        } catch (error) {
            logger.error('IFC_MODEL', '❌ Error al calcular elevación inicial:', error);
            return null;
        }
    }

    /**
     * Método público para calcular la elevación elipsoidal aplicando los offsets en XY
     * Calcula la elevación en las coordenadas finales (con offset aplicado) para obtener
     * la elevación real en la posición donde se ha colocado el modelo
     */
    public async calculateEllipsoidalElevationWithOffsets(useWebService: boolean = false): Promise<number | null> {
        const originalCoordinates = this.getIFCCoordinates();
        if (!originalCoordinates) {
            logger.warn('IFC_MODEL', '⚠️ No se pueden obtener coordenadas del IFC para calcular elevación con offsets');
            return null;
        }

        const offsetThreshold = 0.1;
        if (Math.abs(this.adjustOffsetX) < offsetThreshold && Math.abs(this.adjustOffsetZ) < offsetThreshold) {
            logger.log('IFC_MODEL', `📏 Ajuste fino insignificante, usando coordenadas originales`);
            return await this.calculateEllipsoidalElevationFromCoordinates(
                originalCoordinates.lat,
                originalCoordinates.lon,
                useWebService
            );
        }

        const samplePoint = this.getSampleUTMPoint();
        let finalLat: number;
        let finalLon: number;

        if (samplePoint) {
            const [lonFromSample, latFromSample] = proj4('EPSG:25830', 'EPSG:4326', samplePoint);
            finalLat = latFromSample;
            finalLon = lonFromSample;
            logger.log('IFC_MODEL', `🌍 Coordenadas finales (desde geometría georreferenciada):`);
            logger.log('IFC_MODEL', `   Punto muestreado UTM: X=${samplePoint[0].toFixed(3)} Z=${samplePoint[1].toFixed(3)}`);
        } else {
            const [origUtmX, origUtmZ] = proj4('EPSG:4326', 'EPSG:25830', [originalCoordinates.lon, originalCoordinates.lat]);
            const baseUtmX = this.baseOffsetX !== 0 ? this.baseOffsetX : origUtmX;
            const baseUtmZ = this.baseOffsetZ !== 0 ? this.baseOffsetZ : origUtmZ;
            const finalUtmX = baseUtmX + this.adjustOffsetX;
            const finalUtmZ = baseUtmZ + this.adjustOffsetZ;
            const [backupLon, backupLat] = proj4('EPSG:25830', 'EPSG:4326', [finalUtmX, finalUtmZ]);
            finalLat = backupLat;
            finalLon = backupLon;
            logger.log('IFC_MODEL', `🌍 Coordenadas finales calculadas (fallback base+offset):`);
            logger.log('IFC_MODEL', `   UTM origen estimado: X=${baseUtmX.toFixed(3)} Z=${baseUtmZ.toFixed(3)}`);
            logger.log('IFC_MODEL', `   UTM final: X=${finalUtmX.toFixed(3)} Z=${finalUtmZ.toFixed(3)}`);
        }
        
        logger.log('IFC_MODEL', `   Latitud: ${finalLat.toFixed(6)}°`);
        logger.log('IFC_MODEL', `   Longitud: ${finalLon.toFixed(6)}°`);

        const finalElevation = await this.calculateEllipsoidalElevationFromCoordinates(
            finalLat,
            finalLon,
            useWebService
        );

        if (finalElevation === null || !Number.isFinite(finalElevation)) {
            logger.error('IFC_MODEL', '❌ No se pudo calcular la elevación en coordenadas finales');
            return null;
        }

        // También calcular en coordenadas originales para comparar
        const originalElevation = await this.calculateEllipsoidalElevationFromCoordinates(
            originalCoordinates.lat, 
            originalCoordinates.lon, 
            useWebService
        );

        if (originalElevation !== null && Number.isFinite(originalElevation)) {
            const elevationDifference = finalElevation - originalElevation;
            logger.log('IFC_MODEL', `📏 Comparación de elevaciones:`);
            logger.log('IFC_MODEL', `   Original: ${originalElevation.toFixed(2)} m`);
            logger.log('IFC_MODEL', `   Final: ${finalElevation.toFixed(2)} m`);
            logger.log('IFC_MODEL', `   Diferencia: ${elevationDifference.toFixed(2)} m`);
        }

        return finalElevation;
    }

    public getStoreyGuid(storeyId: string): string | undefined {
        return this.storeysGuids[storeyId];
    }

    public getStoreyByGuid(guid: string): any | undefined {
        return this.storeysByGuid[guid];
    }

    public getStoreyByExpressId(expressId: string): any | undefined {
        return this.storeys[expressId];
    }

    public getAllStoreyGuids(): string[] {
        return Object.values(this.storeysGuids);
    }

}   

